import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    AmbientLight,
    Mesh,
    VideoTexture,
    RGBAFormat,
    LinearFilter,
    PlaneGeometry,
    ShaderMaterial,
    Vector3, TextureLoader, MeshBasicMaterial, DoubleSide,
} from "three";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import {DEFOULT_IDLE_VIDEO} from "../store/constatns";
import {fragmentShader, vertexShader} from "./shaderParams";
import shadow from "../assets/images/shadow.png";

export class MrFirstVideo {

    constructor() {
        this.transparentVideoUrl = "https://space-api.pandamr.io/media/dynamic/added_models/022.mp4";
        this.muted = true;
        this.position = new Vector3(0, -1, -10);
    }

    requestAnimationFrameId = null;
    video = document.createElement("Video");
    videoMesh = null;

    removeVideoMesh() {
        this.video.pause();
        this.video.currentTime = 0;
        this.video = null;
        this.scene.remove(this.videoMesh);
    }

    async getScene() {
        this.video = document.createElement("Video");
        this.scene = new Scene();
        await this.videoTextureManager();
        this.getVideoMesh();
        this.isLoadedTrigger()

        const light = new AmbientLight(0xffffff);

        this.scene.add(light);
        return this.scene;
    };

    getRenderer(canvas) {
        const renderer = new WebGLRenderer({antialias: true, alpha: true, canvas});
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        return renderer;
    }

    getControls(camera, domElement) {
        const controls = new OrbitControls(camera, domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableZoom = false;
        controls.enablePan = false;
        controls.target.set(0, 0, 0);
        return controls;
    };


    getCamera(width, height) {
        const camera = new PerspectiveCamera(45, width / height, 0.1, 100);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();

        camera.position.set(20, 1.5, 0); // Set position like this

        const cameraUpdate = () => {
            camera.aspect = width / height;

            camera.updateProjectionMatrix();
        };

        return {camera, cameraUpdate};
    };

    isVideoPlaying() {
        return !!(this.video.currentTime > 0 && !this.video.paused && !this.video.ended && this.video.readyState > 2);
    }

    isLoadedTrigger = () => {
        this.video.play()
    };

    setIdleVideoUrl() {
        this.video.src = DEFOULT_IDLE_VIDEO;
        this.video.play();
    }

    videoTextureManager() {
        return new Promise((res, rej) => {
            this.video.src = this.transparentVideoUrl;
            this.video.muted = this.muted;
            this.video.setAttribute("crossorigin", "anonymous");
            this.video.setAttribute("webkit-playsinline", "webkit-playsinline");
            this.video.playsInline = "playsInline";
            this.video.onloadedmetadata = () => {
                res();
            };

            this.video.onended = () => {
                this.skipVideo();
            };

            this.video.onerror = (e) => {
                console.log("Error loading video: ", e, `===> ${this.video.src}`);
                rej();
            };
        });
    }

    getVideoMesh() {
        const map = new VideoTexture(this.video);
        map.format = RGBAFormat;
        map.minFilter = LinearFilter;
        map.magFilter = LinearFilter;
        const material = new ShaderMaterial({
            transparent: true,
            uniforms: {
                map: {value: map},
                keyColor: {value: [0, 1, 0]},
                similarity: {value: 0.6},
                smoothness: {value: 0.1},
            },
            vertexShader: vertexShader(),
            fragmentShader: fragmentShader(),
        });

        const geometry = new PlaneGeometry(10, 12.8);
        const mesh = new Mesh(geometry, material);
        mesh.position.set(...this.position);
        mesh.rotation.set(0, Math.PI * 0.5, 0);

        const shadowMap = new TextureLoader().load(shadow);
        const shadowMat = new MeshBasicMaterial({
            side: DoubleSide,
            color: "black",
            transparent: true,
            opacity: 0.65,
            map: shadowMap
        });
        const shadowGeo = new PlaneGeometry(7, 4);
        const shadowMesh = new Mesh(shadowGeo, shadowMat);
        shadowMesh.position.set(...this.position);
        shadowMesh.rotation.set(-Math.PI/2, 0, 0)
        shadowMesh.position.z += 8
        shadowMesh.position.x += 1
        shadowMesh.position.y -= 6
        mesh.add(shadowMesh);

        this.videoMesh = mesh;
        this.scene.add(this.videoMesh);
    }

    setMobilePosition(){
        let isTablet = window.innerWidth < 1100 && window.innerWidth > 600
        let isMobile = window.innerWidth < 600
        if(isTablet){
            this.videoMesh.position.z = -4
        }
        else if(isMobile){
            this.videoMesh.position.z = -2
        }
        else{
            this.videoMesh.position.set(...this.position)
        }
    }

    async initThree(canvas) {
        const WIDTH = window.innerWidth,
            HEIGHT = window.innerHeight;
        const renderer = this.getRenderer(canvas);
        const scene = await this.getScene();
        const {camera, cameraUpdate} = this.getCamera(WIDTH, HEIGHT);
        const controls = this.getControls(camera, renderer.domElement);
        controls.enabled = false;
        const render = () => {
            renderer.render(scene, camera);
            controls.update();
        };
        this.setMobilePosition()

        const onWindowResize = () => {
            this.setMobilePosition()
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();

            renderer.setSize(window.innerWidth, window.innerHeight);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

            render();
        };

        window.onresize = onWindowResize;
        onWindowResize();

        return {renderer, scene, camera, controls, cameraUpdate, render};
    };

    cleanupListener() {
        while (this.scene.children.length > 0) {
            this.scene.remove(this.scene.children[0]);
        }
        this.removeVideoMesh();
        console.log("unmounted!!!");
        window.cancelAnimationFrame(this.requestAnimationFrameId);
    }

    skipVideo() {
        this.setIdleVideoUrl();
    }

    repeatVideo() {
        this.video.src = this.transparentVideoUrl;
        this.video.currentTime = 0;
        this.video.play();
    }

    async execute(canvas) {
        const {render, controls} = await this.initThree(canvas);
        const animate = () => {
            render();
            controls.update();
            this.requestAnimationFrameId = window.requestAnimationFrame(animate);
        };
        this.requestAnimationFrameId = window.requestAnimationFrame(animate);
    };

}